import { useState, useEffect, useRef, useCallback } from 'react';

// ─── Turnstile type declarations ────────────────────────────────────────────
interface TurnstileInstance {
  render: (
    container: string | HTMLElement,
    options: {
      sitekey: string;
      theme?: 'light' | 'dark' | 'auto';
      callback?: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileInstance;
  }
}

// ─── useTurnstile hook ──────────────────────────────────────────────────────

export interface UseTurnstileReturn {
  /** Current captcha token, or null if not yet verified / expired */
  token: string | null;
  /** Reset the widget so the user can re-verify */
  reset: () => void;
  /** Ref to attach to the container element where the widget should render */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Manages a Cloudflare Turnstile captcha widget.
 *
 * @param siteKey  - Turnstile site key
 * @param visible  - Only render when true (e.g. when backup-login panel is open)
 */
export function useTurnstile(siteKey: string, visible: boolean): UseTurnstileReturn {
  const [token, setToken] = useState<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  useEffect(() => {
    if (!visible || !containerRef.current) return;

    function tryRender() {
      if (!window.turnstile || widgetIdRef.current || !containerRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: 'dark',
        callback: (t: string) => setToken(t),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null),
      });
    }

    if (window.turnstile) {
      tryRender();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          tryRender();
        }
      }, 200);
      return () => clearInterval(interval);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, visible]);

  return { token, reset, containerRef };
}
